import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Swords, ShieldCheck } from "lucide-react";
import api, { apiErr } from "@/lib/api";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password reset. You may now sign in.");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-zinc-950">
      <div className="w-full max-w-sm fade-up">
        <div className="flex items-center gap-3 mb-8">
          <Swords className="text-amber-500" size={32} />
          <span className="font-cinzel text-2xl text-amber-300 uppercase tracking-widest">Ludus</span>
        </div>

        {!token ? (
          <div data-testid="reset-invalid-message">
            <h2 className="font-cinzel text-2xl text-zinc-100 mb-2">Invalid link</h2>
            <p className="text-zinc-400 text-sm mb-6">This reset link is missing its token. Request a new one.</p>
            <button data-testid="reset-request-new-btn" onClick={() => navigate("/forgot-password")}
              className="w-full bg-amber-900/30 text-amber-300 border border-amber-700/60 px-5 py-3 uppercase tracking-[0.2em] font-cinzel hover:bg-amber-900/50 transition-all">
              Request New Link
            </button>
          </div>
        ) : done ? (
          <div data-testid="reset-success-message">
            <div className="flex items-center gap-2 text-amber-400 mb-3">
              <ShieldCheck size={22} />
              <h2 className="font-cinzel text-2xl text-zinc-100">Password reset</h2>
            </div>
            <p className="text-zinc-400 text-sm mb-6">Your new password is forged. Step back into the arena.</p>
            <button data-testid="reset-to-login-btn" onClick={() => navigate("/login")}
              className="w-full bg-amber-900/30 text-amber-300 border border-amber-700/60 px-5 py-3 uppercase tracking-[0.2em] font-cinzel hover:bg-amber-900/50 hover:border-amber-500 transition-all">
              Sign In
            </button>
          </div>
        ) : (
          <>
            <h2 className="font-cinzel text-2xl text-zinc-100 mb-1">Set a new password</h2>
            <p className="text-zinc-500 text-sm mb-6">Choose a strong password to secure your ludus.</p>
            <form onSubmit={submit} className="space-y-4">
              <input data-testid="reset-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="New password"
                className="w-full bg-zinc-900 border border-amber-900/40 px-4 py-3 text-zinc-100 placeholder-zinc-600 focus:border-amber-600 outline-none rounded-sm" />
              <input data-testid="reset-confirm-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required placeholder="Confirm new password"
                className="w-full bg-zinc-900 border border-amber-900/40 px-4 py-3 text-zinc-100 placeholder-zinc-600 focus:border-amber-600 outline-none rounded-sm" />
              <button data-testid="reset-submit-btn" disabled={busy} type="submit"
                className="w-full bg-amber-900/30 text-amber-300 border border-amber-700/60 px-5 py-3 uppercase tracking-[0.2em] font-cinzel hover:bg-amber-900/50 hover:border-amber-500 transition-all disabled:opacity-50">
                {busy ? "…" : "Reset Password"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
